import { Types } from "../actionTypes";
import { createSelector } from "@reduxjs/toolkit";
import { Program, ApplicationTypes } from "../../constants";
import { formatPhoneNumber } from "../utils/formatPhoneNumber";
import { Subscriptions } from "@Src/constants";
export const initialState = {
  program: {
    registration_type: null,
    key: null,
    name: null,
    program_logo: null,
    program_color: null,
    program_help_number: Program.helpNumber,
    member_key_label: null,
    external_links: {},
    welcome_text: "",
    ocid: null,
    isAccessPerks: false,
    cashback: {},
    travel_enabled: true,
    travel_tab: {
      hotels_tab_enabled: true,
      cars_tab_enabled: true,
      flights_tab_enabled: true,
      activities_tab_enabled: true,
      theme_park_tab_enabled: true,
    },
    is_standalone_travel: false,
    is_voucher_travel: false,
    email_marketing: null,
    allows_preview: false,
    allows_sla_token: false,
  },
  isLoading: false,
  errorLoading: false,
  sessionExpired: false,
};

function programReducer(state = initialState, action) {
  const { type, data } = action;

  switch (type) {
    case Types.FETCHING_PROGRAM:
      return {
        ...state,
        isLoading: true,
      };
    case Types.FETCHING_PROGRAM_SUCCESS:
      let is_standalone_travel = false;
      let is_voucher_travel = String(process.env.REACT_APP_TRAVEL_ONLY_VOUCHER_PCIDS)
        .split(",")
        .includes(data.program_key);
      if (
        data.application_type === ApplicationTypes.STAND_ALONE_TRAVEL ||
        data.subscription_name.includes(Subscriptions.TRAVELONLY) ||
        is_voucher_travel
      ) {
        is_standalone_travel = true;
      }
      return {
        ...state,
        isLoading: false,
        program: {
          application_type: data.application_type,
          registration_type: data.registration_type,
          approved_email_domains: data.approved_email_domains,
          key: data.program_key,
          name: data.program_name,
          program_logo: data.program_logo_url,
          program_color: data.program_color,
          help_phone_number: data.help_phone_number,
          member_key_label: data.mcid_label,
          member_key_description: data.mcid_label_description,
          code_error_message: data.code_error_message,
          welcome_text: data.welcome_text || "",
          ocid: data.program_organization_cid,
          external_links: data.external_links || {},
          billing_enabled: data.billing_enabled,
          mobile_app_attributes: data.mobile_app_attributes,
          travel_enabled: data.travel.is_enabled,
          travel_tab: data.travel.tab_detail,
          cashback: data.travel.cash_back,
          subscription_name: data.subscription_name,
          isAccessPerks: data.isAccessPerks,
          is_standalone_travel,
          is_voucher_travel,
          email_marketing: data.email_marketing,
          allows_preview: data.allows_preview || false,
          allows_sla_token: data.allows_sla_token || false,
        },
      };
    case Types.FETCHING_PROGRAM_FAILURE:
      return {
        ...state,
        isLoading: false,
        errorLoading: true,
      };
    case Types.SET_SESSION_EXPIRED:
      return {
        ...state,
        sessionExpired: true,
      };
    case Types.SIGN_OUT_SUCCESS:
      return {
        ...state,
        sessionExpired: false,
      };
    case Types.SET_CAT_MENU_CLICKED:
      return {
        ...state,
        catMenuClicked: data,
      };
    default:
      return state;
  }
}

export const getRegistrationType = (state) => state.program.registration_type;
export const getApprovedDomains = (state) => state.program.approved_email_domains;
export const getIsCashback = (state) => {
  const { alternate_currency_name, is_enabled } = state.program.cashback;
  return is_enabled && alternate_currency_name === null;
};
export const getProgram = (state) => state.program;
export const getProgramKey = (state) => state.program.key;
export const getProgramName = (state) => state.program.name;
export const getProgramColor = (state) => state.program.program_color;
export const getProgramMemberKeyLabel = (state) => state.program.member_key_label;
export const getProgramMemberKeyDescription = (state) => state.program.member_key_description;
export const getCodeErrorMsg = (state) => state.program.code_error_message;
export const getProgramWelcomeText = (state) => state.program.welcome_text;
export const getProgramOcid = (state) => state.program.ocid;
export const getProgramLogo = (state) => state.program.program_logo;
export const getAddOnsProgramInfo = createSelector([(state) => state.program], (program) => {
  return {
    key: program.key,
    name: program.name,
    ocid: program.ocid,
  };
});

export const getMobileAppData = (state) => state.program.mobile_app_attributes;
export const getTravelEnabled = (state) => state.program.travel_enabled;
export const getTravelTabsConfig = (state) => state.program.travel_tab;
export const getSubscription = (state) => state.program.subscription_name;
export const getIsAccessPerks = (state) => state.program.isAccessPerks;
export const getSocialLinks = (state) => {
  const { external_links } = state.program;
  if (
    external_links.FACEBOOK ||
    external_links.LINKED_IN ||
    external_links.INSTAGRAM ||
    external_links.TWITTER
  ) {
    return external_links;
  }
  return null;
};
export const getIsStandaloneTravel = (state) => state.program.is_standalone_travel;
export const getIsVoucherTravel = (state) => state.program.is_voucher_travel;
export const getHelpPhoneNumber = (state) => {
  if (state.program.is_standalone_travel) {
    return state.program.help_phone_number.replace(/[\s-()]/g, "").includes("4082603")
      ? `(877) 428-4585`
      : state.program.help_phone_number;
  }
  return formatPhoneNumber(state.program.help_phone_number);
};
export const getIsThemeParksEnabled = (state) => {
  return state.program?.travel_tab?.theme_park_tab_enabled;
};
export const getIsSessionExpired = (state) => state.sessionExpired;
export const getIsOnlyThemeParksEnabled = (state) => {
  const { theme_park_tab_enabled, ...restOfTabs } = state.program.travel_tab;

  return (
    Object.values(restOfTabs).every((enabledTab) => enabledTab === false) && theme_park_tab_enabled
  );
};
export const getProgramEmailMarketing = (state) => state.program.email_marketing;
export const getIsPreviewProgram = (state) => state.program.allows_preview;
export const getIsSlaTokenEnabled = (state) => state.program.allows_sla_token;

export default programReducer;
